import React, { useEffect, useState } from "react";
import { Box, Card, Modal, Grid, Fade, Typography } from "@mui/material";

import ServiceCard from "../components/ServiceCard";
import ContactPage from "./ContactPage";

import { theme } from "../services/Theme";
import { Storage } from "aws-amplify";
import SEO from "../components/SEO";

const serviceImageKeys = {
  powderCoating: "services/PC.png",
  welding: "services/Welding.png",
  plasmaCutting: "services/PlasmaCutting.png",
  cerakote: "services/Cerakote.png",
  fabrication: "services/Fabrication.png",
  customSigns: "services/CustomSigns.png",
  printing: "services/Printing.png",
  modeling: "services/CADModeling.png",
};

function ServicesPage({ openSuccessMessage }) {
  const [contactUsState, setContactUsState] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [serviceImages, setServiceImages] = useState({});

  const openContactForm = (serviceKey) => {
    setSelectedService(serviceKey || null);
    setContactUsState(true);
  };
  const closeContactForm = () => {
    setContactUsState(false);
    setSelectedService(null);
  };

  useEffect(() => {
    async function fetchServiceImages() {
      try {
        const resolvedImages = await Promise.all(
          Object.entries(serviceImageKeys).map(async ([service, key]) => {
            const signedUrl = await Storage.get(key, { level: "public" });
            return [service, signedUrl];
          }),
        );

        setServiceImages(Object.fromEntries(resolvedImages));
      } catch (error) {
        console.error("Unable to load service images from S3", error);
      }
    }

    fetchServiceImages();
  }, []);

  return (
    <Grid
      container
      spacing={{ sm: 3, xs: 2 }}
      style={{
        width: "100%",
        alignItems: "stretch",
        justifyContent: "flex-start",
      }}
    >
      <SEO
        title="Fabrication & Finishing Services"
        description="HaasFab offers powder coating, Cerakote application, CNC plasma cutting, welding, custom metal signage, fabrication, 3D printing, and CAD modeling."
        path="/"
      />
      <Grid item sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
        <ServiceCard
          serviceImage={serviceImages.powderCoating}
          serviceTitle="Powder Coating"
          serviceKey="Powder Coating"
          serviceDescription={
            <div>
              We provide complete powder coating services for both restoration and new fabrication projects,
              including: <br /> <br /> - Chemical stripping <br /> - Sandblasting and surface prep <br /> -
              Primer and phosphate pretreatment options <br /> - Multi-stage finishes for improved durability
              <br />
            </div>
          }
          openContactForm={openContactForm}
        />
      </Grid>

      <Grid item sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
        <ServiceCard
          serviceImage={serviceImages.welding}
          serviceTitle="Welding Services"
          serviceKey="Welding"
          serviceDescription="We offer TIG and MIG welding for mild steel, stainless steel, and aluminum. Whether you need repair work, custom components, or production-ready weldments, we focus on clean fitment and long-term reliability."
          openContactForm={openContactForm}
        />
      </Grid>

      <Grid item sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
        <ServiceCard
          serviceImage={serviceImages.plasmaCutting}
          serviceTitle="CNC Plasma Cutting"
          serviceKey="CNC Plasma Cutting"
          serviceDescription='Our 48"x33" CNC plasma table produces accurate, repeatable cuts in up to 3/4" steel and 1/2" aluminum. It is ideal for custom brackets, plates, repair sections, and signs, and pairs well with powder coating or Cerakote for a durable finished product.'
          openContactForm={openContactForm}
        />
      </Grid>

      <Grid item sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
        <ServiceCard
          serviceImage={serviceImages.cerakote}
          serviceTitle="Cerakote Application"
          serviceKey="Cerakote Application"
          serviceDescription='Cerakote is a thin-film ceramic coating that provides excellent resistance to heat, chemicals, abrasion, and corrosion. Its low build thickness helps preserve part tolerances while delivering a high-performance finish.'
          openContactForm={openContactForm}
        />
      </Grid>

      <Grid item sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
        <ServiceCard
          serviceImage={serviceImages.fabrication}
          serviceTitle="Fabrication Services"
          serviceKey="Fabrication"
          serviceDescription="We handle small to medium fabrication projects with an emphasis on automotive and light equipment applications. From one-off parts to complete assemblies, we can support your project from concept through final fitment."
          openContactForm={openContactForm}
        />
      </Grid>
      <Grid item sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
        <ServiceCard
          serviceImage={serviceImages.customSigns}
          serviceTitle="Custom Metal Signs"
          serviceKey="Custom Metal Signage"
          serviceDescription="We build single-layer, multilayer, and backlit metal signs up to 4 ft x 5 ft. For long-term outdoor durability and appearance, we recommend pairing each sign with powder coating or Cerakote."
          openContactForm={openContactForm}
        />
      </Grid>
      <Grid item sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
        <ServiceCard
          serviceImage={serviceImages.printing}
          serviceTitle="3D Printing"
          serviceKey="3D Printing"
          serviceDescription="Our 3D printing service supports rapid prototyping, functional testing, and low-volume production parts. With a 400 mm x 400 mm x 250 mm build area and materials like PETG, PLA, TPU, ABS, and ASA, we can match material selection to your application."
          openContactForm={openContactForm}
        />
      </Grid>
      <Grid item sm={6} xs={12} sx={{ display: "flex", justifyContent: "center" }}>
        <ServiceCard
          serviceImage={serviceImages.modeling}
          serviceTitle="CAD Modeling"
          serviceKey="CAD Modeling"
          serviceDescription="If you have an idea, sketch, or reference photos, we can develop production-ready CAD models and drawings. CAD services integrate directly with our 3D printing and CNC plasma cutting workflows for faster project turnaround."
          openContactForm={openContactForm}
        />
      </Grid>

      <Grid item xs={12} sx={{ display: "flex", justifyContent: "center" }}>
        <Card
          sx={{
            width: "100%",
            maxWidth: "900px",
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            backgroundColor: theme.palette.background.main,
            textAlign: "center",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.text.light,
              fontWeight: 700,
              mb: 1,
            }}
          >
            Areas We Serve
          </Typography>
          <Typography
            sx={{
              color: theme.palette.text.light,
              fontSize: { xs: "0.9rem", sm: "1rem" },
              lineHeight: 1.6,
            }}
          >
            HaasFab is based in Bowling Green, Ohio, and proudly serves customers throughout
            Northwest Ohio — including Toledo, Perrysburg, Maumee, Findlay, Fremont, and
            surrounding communities.
          </Typography>
        </Card>
      </Grid>

      <Modal
        open={contactUsState}
        onClose={closeContactForm}
        closeAfterTransition
        slotProps={{
          backdrop: {
            timeout: 220,
          },
        }}
      >
        <Fade in={contactUsState} timeout={220}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",

              bgcolor: "background.paper",
              border: "2px solid #000",
              display: "flex",
            }}
          >
            <ContactPage
              closeContactForm={closeContactForm}
              openSuccessMessage={openSuccessMessage}
              preselectedService={selectedService}
            />
          </Box>
        </Fade>
      </Modal>
    </Grid>
  );
}

export default ServicesPage;
